/**
 * API client configuration for the animation platform
 * Configured to work with our Fastify backend
 */

import axios from 'axios';
import toast from 'react-hot-toast';

// Base API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const API_TIMEOUT = import.meta.env.VITE_API_TIMEOUT || 240000; // Increased to 4 minutes for Grok-4 Fast validation

// Create axios instance with proper config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for CORS with credentials
});

// Token management
let authToken = null;

export const setAuthToken = (token) => {
  authToken = token;
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('authToken', token);
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
    localStorage.removeItem('authToken');
  }
};

// Initialize token from localStorage
const savedToken = localStorage.getItem('authToken');
if (savedToken) {
  setAuthToken(savedToken);
}

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add request timestamp for debugging
    config.metadata = { startTime: Date.now() };
    
    // Log request in development (excluding progress tracking to reduce noise)
    if (import.meta.env.DEV && !config.url?.includes('/progress/track')) {
    }
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Calculate response time
    const responseTime = Date.now() - response.config.metadata.startTime;
    
    // Log response in development (excluding progress tracking to reduce noise)
    if (import.meta.env.DEV && !response.config.url?.includes('/progress/track')) {
    }
    
    // Return the response data directly if it follows our standard format
    if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      return response.data;
    }
    
    // Return full response for non-standard responses
    return response;
  },
  (error) => {
    const responseTime = error.config?.metadata 
      ? Date.now() - error.config.metadata.startTime 
      : 0;
    
    // Log error in development
    if (import.meta.env.DEV) {
      console.error(`❌ API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
        status: error.response?.status,
        responseTime: `${responseTime}ms`,
        message: error.message,
        data: error.response?.data,
      });
    }
    
    // Handle specific error cases
    const isAuthEndpoint = error.config?.url?.includes('/auth/') || error.config?.url?.includes('/signup/');
    
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      setAuthToken(null);
      
      // Only show toast if not already on auth pages
      if (!window.location.pathname.includes('/signin') && !window.location.pathname.includes('/signup')) {
        toast.error('Your session has expired. Please sign in again.');
        window.location.href = '/signin';
      }
    } else if (error.response?.status === 403) {
      // Forbidden
      if (!isAuthEndpoint) {
        toast.error('You don\'t have permission to perform this action.');
      }
    } else if (error.response?.status === 404) {
      // Not found - don't show generic toast for auth endpoints
      if (!isAuthEndpoint) {
        toast.error('The requested resource was not found.');
      }
    } else if (error.response?.status === 409) {
      // Conflict - don't show generic toast, let the calling code handle it
      // This is often used for "already exists" errors in auth
    } else if (error.response?.status >= 500) {
      // Server errors - don't show generic toast for auth endpoints
      if (!isAuthEndpoint) {
        toast.error('Server error. Please try again later.');
      }
    } else if (error.code === 'ECONNABORTED') {
      // Timeout
      toast.error('Request timeout. Please check your connection.');
    } else if (!error.response) {
      // Network error
      if (!isAuthEndpoint) {
        toast.error('Network error. Please check your connection.');
      }
    }
    
    // Return standardized error
    const standardError = {
      success: false,
      message: error.response?.data?.message || error.message || 'An error occurred',
      statusCode: error.response?.status || 0,
      data: error.response?.data || null,
    };
    
    return Promise.reject(standardError);
  }
);

// API helper functions
export const api = {
  // Generic methods
  get: (url, params = {}) => apiClient.get(url, { params }),
  post: (url, data = {}, config = {}) => apiClient.post(url, data, config),
  put: (url, data = {}) => apiClient.put(url, data),
  delete: (url) => apiClient.delete(url),
  patch: (url, data = {}) => apiClient.patch(url, data),
  // Post FormData without re-wrapping; supports progress
  postForm: (url, formData, onProgress) =>
    apiClient.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    }),
  
  // Health check
  health: () => apiClient.get('/health'),
  
  // File upload helper
  upload: (url, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);

    return apiClient.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });
  },
  
  // Batch requests helper
  batch: async (requests) => {
    try {
      const responses = await Promise.allSettled(
        requests.map(request => 
          apiClient.request(request)
        )
      );
      
      return responses.map((response, index) => ({
        success: response.status === 'fulfilled',
        data: response.status === 'fulfilled' ? response.value : null,
        error: response.status === 'rejected' ? response.reason : null,
        request: requests[index],
      }));
    } catch (error) {
      console.error('Batch request error:', error);
      throw error;
    }
  },
};

// Request cancellation helper
export const createCancelToken = () => {
  const source = axios.CancelToken.source();
  return {
    token: source.token,
    cancel: source.cancel,
  };
};

// Retry helper for failed requests
export const retryRequest = async (requestFn, maxRetries = 3, delay = 1000) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      if (attempt === maxRetries || error.statusCode < 500) {
        throw error;
      }
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay * attempt));
    }
  }
};

// Export configured client and helpers (remove duplicate export)
export default api;